/**
 * @file verification.ts
 * @description Коды подтверждения сайта в Яндекс Вебмастере и Google Search
 * Console (phase19.16, партия Р3в, v0.19.64; seo.md SEO1, SEO2; SB145):
 * владелец вставляет код или мета-тег целиком — платформа достаёт код; до 5
 * кодов на поисковик. Общий разбор для кабинета (подсказка при вводе) и api
 * (проверка при сохранении) — один формат на обеих сторонах.
 *
 * Формат кода: латиница, цифры, «_» и «-», 8–100 знаков (Яндекс — 16
 * шестнадцатеричных, Google — 43 знака base64url). Строже прежнего
 * `SITE_VERIFICATION_PATTERN` (site-theme/snapshot.ts): в `<head>` уходит
 * только то, что не может сломать разметку.
 */

export type SeoVerificationEngine = "yandex" | "google";

/** До 5 кодов на поисковик: владелец, агентство, сотрудник (SEO1). */
export const SEO_VERIFICATION_MAX = 5;

/** Имя мета-тега подтверждения у поисковика. */
export const SEO_VERIFICATION_META_NAMES: Readonly<Record<SeoVerificationEngine, string>> = Object.freeze({
  yandex: "yandex-verification",
  google: "google-site-verification",
});

export const SEO_VERIFICATION_CODE_PATTERN = /^[A-Za-z0-9_-]{8,100}$/u;

/** Атрибуты тега: `name="x"`, `name='x'`, `name=x`. */
const ATTRIBUTE = /([a-z-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/giu;

function metaAttributes(tag: string): Map<string, string> {
  const attrs = new Map<string, string>();
  for (const match of tag.matchAll(ATTRIBUTE)) {
    const name = match[1]?.toLowerCase();
    const value = match[2] ?? match[3] ?? match[4] ?? "";
    if (name && !attrs.has(name)) attrs.set(name, value.trim());
  }
  return attrs;
}

/**
 * Код подтверждения из ввода владельца: сам код или мета-тег целиком
 * (`<meta name="yandex-verification" content="…" />`). Тег другого
 * поисковика, тег без `content`, мусор и код не по формату — null.
 */
export function parseVerificationCode(input: string | null | undefined, engine: SeoVerificationEngine): string | null {
  const text = (input ?? "").trim();
  if (!text) return null;
  let code = text;
  if (text.includes("<")) {
    const tag = /^<meta\b([^<>]*)\/?>$/iu.exec(text);
    if (!tag) return null;
    const attrs = metaAttributes(tag[1] ?? "");
    if (attrs.get("name")?.toLowerCase() !== SEO_VERIFICATION_META_NAMES[engine]) return null;
    code = attrs.get("content") ?? "";
  }
  return SEO_VERIFICATION_CODE_PATTERN.test(code) ? code : null;
}

/** Коды ошибок списка (snake_case, общие для api и кабинета). */
export type SeoVerificationListError = "seo_verification_invalid" | "seo_verification_limit";

export type ParseVerificationCodesResult =
  | { data: string[]; error: null }
  | { data: null; error: SeoVerificationListError; index?: number };

/**
 * Список кодов одного поисковика: пустые строки пропускаются, повторы
 * склеиваются; любой неразборчивый ввод — `seo_verification_invalid` с его
 * номером, больше 5 кодов — `seo_verification_limit`.
 */
export function parseVerificationCodes(
  inputs: readonly (string | null | undefined)[],
  engine: SeoVerificationEngine,
): ParseVerificationCodesResult {
  const codes: string[] = [];
  for (let index = 0; index < inputs.length; index += 1) {
    const raw = inputs[index];
    if (!raw || raw.trim().length === 0) continue;
    const code = parseVerificationCode(raw, engine);
    if (code === null) return { data: null, error: "seo_verification_invalid", index };
    if (!codes.includes(code)) codes.push(code);
  }
  if (codes.length > SEO_VERIFICATION_MAX) return { data: null, error: "seo_verification_limit" };
  return { data: codes, error: null };
}
